import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import Image from '@segment/matcha/components/Image'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'
import ButtonText from '@segment/matcha/components/ButtonText'

import * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import imageWebp from './assets/EmbraceGoodData.webp'
import imageWebp2x from './assets/EmbraceGoodData@2x.webp'
import imagePng2x from './assets/EmbraceGoodData@2x.png'

const copy = {
  heading: `Embrace the power of good data`,
  description: [
    `Data is the only resource capable of the speed and scale required to deliver relevant, real-time experiences to every customer.`,
    `Achieving good data within an organization has become increasingly complex. Learn how to avoid the common pitfalls of bad data, and empower your business with trusted, real-time insights.`,
  ],
  button: {
    text: `Download now`,
    href: `https://segment.com/learn/download/why-bad-data-happens-to-good-companies/?utm_source=good-data-hub&utm_medium=referral&utm_campaign=all_ic_2024-05-16-cnt-good%20data-why-bad-data-happens-to-good-companies%20_20240516&utm_content=ebook`,
  },
}

const EmbraceGoodData: FC<PropsWithChildren<{}>> = () => {
  const pageConfig = usePageConfig()

  return (
    <Wrapper>
      <StyledSection variant="2Col">
        <Section.Copy alignSelf="center">
          <CopyText.Heading variant="h2" tag="h2">
            {copy.heading}
          </CopyText.Heading>

          {copy.description?.map((description, index) => (
            <CopyText.Description variant="homePLarge" tag="p" key={index}>
              {description}
            </CopyText.Description>
          ))}
          <ButtonWrapper>
            <ButtonText
              variant="primary"
              size="medium"
              href={getAbsoluteUrl(`${copy.button.href}`, pageConfig)}
              iconEnd={ChevronRightIcon}
            >
              {copy.button.text}
            </ButtonText>
          </ButtonWrapper>
        </Section.Copy>
        <Section.Contents>
          <div>
            <picture>
              <source type="image/webp" srcSet={`${imageWebp}, ${imageWebp2x} 2x`} />
              <Image
                width="640"
                height="470"
                src={imagePng2x}
                alt="Illustration: Embrace the power of good data"
                loading="lazy"
              />
            </picture>
          </div>
        </Section.Contents>
      </StyledSection>
    </Wrapper>
  )
}

const Wrapper = styled.div``

const StyledSection = styled(SectionBase)`
  ${Section.Wrapper} {
    gap: 80px;

    ${({ theme }) => theme.breakpoints.down(`md`)} {
      gap: 40px;
    }
  }
`

const ButtonWrapper = styled.div`
  margin-top: 20px;
`

export default EmbraceGoodData
